// Ventas is the sole contact source. Purchasing supplier identities remain unchanged.
const companyKey = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

export async function readSalesCompanies(crm) {
    const companies = [], seen = new Set();
    let total;
    for (let page = 1; page <= 10000; page++) {
        const result = await crm(`/companies?page=${page}&pageSize=100`);
        if (!Array.isArray(result?.items) || !Number.isSafeInteger(result.total) || result.total < 0 || (total !== undefined && result.total !== total)) throw new Error('El directorio de Ventas cambió o está incompleto. Actualiza los datos.');
        total = result.total;
        for (const company of result.items) {
            if (!company._id || seen.has(String(company._id))) throw new Error('Ventas devolvió empresas repetidas o sin identificador. Actualiza los datos.');
            seen.add(String(company._id));
            companies.push({_id: company._id, name: company.name, quickBooksNames: company.quickBooksNames, contacts: company.contacts});
        }
        if (companies.length === total) return companies;
        if (companies.length > total || result.items.length < 100) break;
    }
    throw new Error('No se pudo consultar el directorio completo de Ventas.');
}

export function withSalesContacts(suppliers, companies) {
    const byName = new Map();
    for (const company of companies) {
        for (const key of new Set([company.name, ...(company.quickBooksNames || [])].map(companyKey).filter(Boolean))) {
            if (!byName.has(key)) byName.set(key, new Map());
            byName.get(key).set(String(company._id), company);
        }
    }
    return suppliers.map(supplier => {
        const key = companyKey(supplier.name);
        const exact = [...(byName.get(key)?.values() || [])];
        // A shorter commercial name needs corroboration by an existing exact email.
        // That email identifies the company only; displayed contacts still come from Ventas.
        const referenceEmails = new Set([...(supplier.emails || []), ...(supplier.contacts || []).map(c => c.email)].filter(Boolean).map(e => String(e).trim().toLowerCase()));
        const matches = exact.length ? exact : companies.filter(c => {
            const candidate = companyKey(c.name);
            return Math.min(key.length, candidate.length) >= 3 && (key.startsWith(candidate) || candidate.startsWith(key)) &&
                (c.contacts || []).some(contact => referenceEmails.has(String(contact.email || '').trim().toLowerCase()));
        });
        const company = matches.length === 1 ? matches[0] : null;
        const contacts = (company?.contacts || []).map(c => ({name: c.name || '', email: String(c.email || '').trim(), phone: c.phone || '', position: c.position || ''}));
        const emails = [...new Map(contacts.filter(c => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email)).map(c => [c.email.toLowerCase(), c.email])).values()];
        return {...supplier, contact: contacts.map(c => c.name).filter(Boolean).join(', '), contacts: contacts.map(c => ({...c, email: emails.includes(c.email) ? c.email : ''})), emails,
            salesCompanyId: company?._id || null,
            contactSource: company ? 'Ventas · Clientes y contactos' : matches.length ? 'Coincidencia ambigua en Ventas' : 'Sin coincidencia en Ventas'};
    });
}

export async function loadSourcingContacts(api, businessUnit) {
    const result = await api(`draft-sourcing-contacts?businessUnit=${businessUnit}`);
    if (result?.businessUnit !== businessUnit || !Array.isArray(result.companies)) throw new Error('Contactos de Ventas incompletos o fuera de alcance.');
    return result.companies;
}
